// Global partner filter, rendered next to the window selector. A tenant is
// one commercial party (see api/admin/tenants.py); picking one scopes every
// windowed panel to that partner's embed origins.
//
// Deliberately a <select>, not a pill row like the window selector: the tenant
// list grows with every onboarded partner, so a fixed-width control is the one
// that survives twenty of them. The two built-in buckets are separated into
// their own group — they are diagnostics ("who is unattributed?"), not
// partners, and grouping them keeps a reader from mistaking them for one.

import { api } from "@/api/client";
import type { WindowLabel } from "@/api/types";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatInt, tenantLabel } from "@/lib/format";
import { includeTestSessions, selectedTenant } from "@/state/filters";

// Wide + fixed: the selector is a directory of partners, not a view of the
// current window, so it must not gain and lose entries as the window changes.
const DIRECTORY_WINDOW: WindowLabel = "365d";

export function TenantSelector() {
  const { data } = usePoll(
    (s) =>
      api.tenants(
        { window: DIRECTORY_WINDOW, includeTest: includeTestSessions.value },
        s,
      ),
    [includeTestSessions.value],
  );

  // Nothing to choose between until a mapping exists — rendering a one-entry
  // dropdown would imply a dimension that isn't configured yet.
  if (!data?.mapping_configured) return null;

  // Not `t` — that name belongs to the translate function in this module.
  const partners = data.tenants.filter((info) => info.configured);
  const buckets = data.tenants.filter((info) => !info.configured);

  return (
    <div class="toolbar">
      <span class="muted">{t("header.partner")}</span>
      <select
        class="tenant-select"
        value={selectedTenant.value}
        onInput={(e) => (selectedTenant.value = (e.target as HTMLSelectElement).value)}
        title={t("header.partnerTitle")}
      >
        <option value="">{t("header.partnerAll")}</option>
        <optgroup label={t("header.partnerGroup")}>
          {partners.map((p) => (
            <option key={p.tenant} value={p.tenant}>
              {tenantLabel(p.tenant)} ({formatInt(p.sessions)})
            </option>
          ))}
        </optgroup>
        <optgroup label={t("header.bucketGroup")}>
          {buckets.map((b) => (
            <option key={b.tenant} value={b.tenant}>
              {tenantLabel(b.tenant)} ({formatInt(b.sessions)})
            </option>
          ))}
        </optgroup>
      </select>
    </div>
  );
}
